import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Typography,
  Box,
  Divider,
  Card,
  CardContent,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Autocomplete,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  CircularProgress,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  FileText,
  Plus,
  Edit,
  X,
  User,
  Building,
  MapPin,
  Calendar,
  DollarSign,
  Package,
  Truck,
  Save,
  RefreshCw,
  AlertCircle,
  Ship,
  Globe,
  FileSignature,
  Upload,
  Trash2,
  Image,
  Archive,
  Download,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Info,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../components/common/Notification';
import { certificatesService } from '../../services/certificates';
import CustomButton from '../../components/ui/CustomButton';
import IncompleteFieldsWarning from '../../components/common/IncompleteRequiredFields';
import ImportCertificateDialog from '../../components/common/ImportCertificateDialog';
import ImportItemsDialog from '../../components/common/ImportItemsDialog';

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// File type icons mapping
const getFileIcon = fileName => {
  if (!fileName || typeof fileName !== 'string') {
    return <FileText size={16} color="#6b7280" />;
  }

  const extension = fileName.split('.').pop()?.toLowerCase();
  if (!extension) {
    return <FileText size={16} color="#6b7280" />;
  }

  switch (extension) {
    case 'pdf':
      return <FileText size={16} color="#ef4444" />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
      return <Image size={16} color="#10b981" />;
    case 'zip':
    case 'rar':
    case '7z':
      return <Archive size={16} color="#f59e0b" />;
    default:
      return <FileText size={16} color="#6b7280" />;
  }
};

// Format file size
const formatFileSize = bytes => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Certificate Item Form Component
const CertificateItemForm = React.memo(
  ({ item, index, onUpdate, onRemove, isDarkMode, errors }) => {
    const handleChange = useCallback(
      (field, value) => {
        const updatedItem = { ...item, [field]: value };
        onUpdate(index, updatedItem, field); // Pass the field name
      },
      [onUpdate, index, item]
    );

    const handleRemoveClick = useCallback(() => {
      onRemove(index);
    }, [onRemove, index]);

    return (
      <Card
        sx={{
          mb: 3,
          background: isDarkMode
            ? 'rgba(30, 41, 59, 0.5)'
            : 'rgba(255, 255, 255, 0.5)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: isDarkMode ? '#f1f5f9' : '#1e293b' }}
            >
              Item {index + 1}
              {/* Show selected type in header if available */}
              {item.Type && (
                <Chip
                  label={item.Type}
                  size="small"
                  sx={{
                    ml: 2,
                    background:
                      'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              )}
            </Typography>
            <IconButton
              onClick={handleRemoveClick}
              size="small"
              sx={{ color: '#ef4444' }}
            >
              <X size={16} />
            </IconButton>
          </Box>

          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {/* NEW: Item Type Field - FIRST and REQUIRED */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                error={!!errors?.[`items.${index}.Type`]}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDarkMode
                      ? 'rgba(59, 130, 246, 0.05)'
                      : 'rgba(59, 130, 246, 0.02)',
                    borderColor: errors?.[`items.${index}.Type`]
                      ? '#ef4444'
                      : undefined,
                  },
                }}
              >
                <InputLabel>🏷️ Item Type *</InputLabel>
                <Select
                  value={item.Type || ''}
                  onChange={e => handleChange('Type', e.target.value)}
                  label="🏷️ Item Type *"
                >
                  <MenuItem value="">
                    <em>Select Item Type</em>
                  </MenuItem>
                  {itemTypeOptions.map(type => (
                    <MenuItem key={type} value={type}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {/* Add icons for each type */}
                        {type === 'Textile' && '🧵'}
                        {type === 'Construction' && '🏗️'}
                        {type === 'Safety' && '🦺'}
                        {type === 'Food' && '🍎'}
                        {type === 'Chemical' && '⚗️'}
                        {type === 'Engineering' && '⚙️'}
                        <span style={{ marginLeft: 8 }}>{type}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors?.[`items.${index}.Type`] && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, ml: 1, display: 'flex', alignItems: 'center' }}
                  >
                    <AlertCircle size={14} style={{ marginRight: 4 }} />
                    {errors[`items.${index}.Type`]}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* EXISTING FIELDS - All remain the same */}
            <Grid item xs={12}>
              <TextField
                label="Item Description *"
                fullWidth
                value={item.ItemDescription || ''}
                onChange={e => handleChange('ItemDescription', e.target.value)}
                error={!!errors?.[`items.${index}.ItemDescription`]}
                helperText={errors?.[`items.${index}.ItemDescription`]}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Packing Method"
                fullWidth
                required
                error={!!errors?.[`items.${index}.Packing`]}
                value={item.Packing || ''}
                onChange={e => handleChange('Packing', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Batch Number"
                fullWidth
                value={item.BatchNumber || ''}
                onChange={e => handleChange('BatchNumber', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Slaughter Date"
                type="date"
                fullWidth
                value={
                  item.SlotDate
                    ? new Date(item.SlotDate).toISOString().split('T')[0]
                    : ''
                }
                onChange={e => {
                  const isoString = new Date(e.target.value).toISOString();
                  handleChange('SlotDate', isoString);
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Quantity *"
                fullWidth
                value={item.Quantity || ''}
                onChange={e => handleChange('Quantity', e.target.value)}
                error={!!errors?.[`items.${index}.Quantity`]}
                helperText={errors?.[`items.${index}.Quantity`]}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Origin *"
                fullWidth
                value={item.Origin || ''}
                onChange={e => handleChange('Origin', e.target.value)}
                error={!!errors?.[`items.${index}.Origin`]}
                helperText={errors?.[`items.${index}.Origin`]}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Goods Value *"
                type="number"
                fullWidth
                value={item.GoodsValue || ''}
                onChange={e =>
                  handleChange('GoodsValue', parseFloat(e.target.value) || 0)
                }
                error={!!errors?.[`items.${index}.GoodsValue`]}
                helperText={errors?.[`items.${index}.GoodsValue`]}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                error={!!errors?.[`items.${index}.Currency`]}
              >
                <InputLabel>Currency *</InputLabel>
                <Select
                  value={item.Currency || 'USD'}
                  onChange={e => handleChange('Currency', e.target.value)}
                  label="Currency *"
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="IQD">IQD</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="AED">AED</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Standard Reference Number *"
                fullWidth
                value={item.StandardRefNumber || ''}
                onChange={e =>
                  handleChange('StandardRefNumber', e.target.value)
                }
                error={!!errors?.[`items.${index}.StandardRefNumber`]}
                helperText={errors?.[`items.${index}.StandardRefNumber`]}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Brand"
                fullWidth
                value={item.Brand || ''}
                onChange={e => handleChange('Brand', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Production Date"
                fullWidth
                value={item.ProductionDate || ''}
                onChange={e => handleChange('ProductionDate', e.target.value)}
                placeholder="e.g., January 2024"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Expiration Date"
                fullWidth
                value={item.ExpirationDate || ''}
                onChange={e => handleChange('ExpirationDate', e.target.value)}
                placeholder="e.g., January 2026"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  }
);
// Attachment Upload Component
const AttachmentUploadSection = ({
  isDarkMode,
  onAttachmentsChange,
  attachments = [],
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = event => {
    const files = Array.from(event.target.files);

    files.forEach(file => {
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
        'application/zip',
        'application/x-zip-compressed',
      ];

      if (!allowedTypes.includes(file.type)) {
        setError(
          'Only PDF, images (JPG, PNG, GIF, WEBP), and ZIP files are allowed.'
        );
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB.');
        return;
      }

      // Add file to attachments list
      const newAttachment = {
        id: Math.random().toString(36),
        file: file,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        description: '',
        isNew: true,
      };

      onAttachmentsChange([...attachments, newAttachment]);
      setError('');
    });

    // Clear the input
    event.target.value = '';
  };

  const handleRemoveAttachment = attachmentId => {
    onAttachmentsChange(attachments.filter(att => att.id !== attachmentId));
  };

  const handleDescriptionChange = (attachmentId, description) => {
    onAttachmentsChange(
      attachments.map(att =>
        att.id === attachmentId ? { ...att, description } : att
      )
    );
  };

  return (
    <Box
      sx={{
        mb: 4,
        p: 3,
        background: isDarkMode
          ? 'rgba(30, 41, 59, 0.6)'
          : 'rgba(255, 255, 255, 0.8)',
        border: isDarkMode
          ? '1px solid rgba(148, 163, 184, 0.1)'
          : '1px solid rgba(148, 163, 184, 0.2)',
        borderRadius: 2,
        backdropFilter: 'blur(10px)',
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: isDarkMode ? '#f1f5f9' : '#1e293b',
          mb: 2,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Upload size={20} style={{ marginRight: 8 }} />
        Upload Attachments
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.zip"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          id="attachment-upload"
          multiple
        />
        <label htmlFor="attachment-upload">
          <Button
            variant="outlined"
            component="span"
            startIcon={<Upload size={16} />}
            sx={{
              borderColor: isDarkMode ? '#475569' : '#d1d5db',
              color: isDarkMode ? '#e2e8f0' : '#374151',
              borderStyle: 'dashed',
              '&:hover': {
                borderColor: '#3b82f6',
                backgroundColor: 'rgba(59, 130, 246, 0.05)',
              },
            }}
          >
            Choose Files
          </Button>
        </label>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1,
            color: isDarkMode ? '#94a3b8' : '#6b7280',
          }}
        >
          Supported: PDF, Images (JPG, PNG, GIF, WEBP), ZIP files • Max 10MB
          each
        </Typography>
      </Box>

      {attachments.length > 0 && (
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              color: isDarkMode ? '#e2e8f0' : '#1f2937',
              mb: 2,
              fontWeight: 600,
            }}
          >
            Selected Files ({attachments.length})
          </Typography>
          <List sx={{ p: 0 }}>
            {attachments.map(attachment => (
              <ListItem
                key={attachment.id}
                sx={{
                  mb: 1,
                  p: 2,
                  border: '1px solid',
                  borderColor: isDarkMode ? '#475569' : '#d1d5db',
                  borderRadius: 1,
                  backgroundColor: isDarkMode
                    ? 'rgba(30, 41, 59, 0.3)'
                    : 'rgba(249, 250, 251, 0.8)',
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {getFileIcon(attachment.fileName)}
                </ListItemIcon>

                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: isDarkMode ? '#e2e8f0' : '#1f2937',
                      }}
                    >
                      {attachment.fileName}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: isDarkMode ? '#94a3b8' : '#6b7280' }}
                      >
                        {formatFileSize(attachment.fileSize)}
                      </Typography>
                      <TextField
                        size="small"
                        placeholder="Description (optional)"
                        value={attachment.description}
                        onChange={e =>
                          handleDescriptionChange(attachment.id, e.target.value)
                        }
                        sx={{
                          mt: 1,
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.875rem',
                          },
                        }}
                      />
                    </Box>
                  }
                />

                <ListItemSecondaryAction>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveAttachment(attachment.id)}
                    sx={{ color: '#ef4444' }}
                  >
                    <X size={16} />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
};

// Point of entry options
const pointOfEntryOptions = [
  'Mosul Dam Crossing',
  'Grand Faw Port',
  'Mandali Border Crossing',
  'Shalamcheh Border Crossing',
  'Safwan Border Crossing',
  'Al-Munthiriya Border Crossing',
  'Trebil Border Crossing',
  'Arar Border Crossing',
  'Al-Sheib Border Crossing',
  'Zurbatiya Border Crossing',
  'Umm Qasr Port',
];

const itemTypeOptions = [
  'Textile',
  'Construction',
  'Safety',
  'Food',
  'Chemical',
  'Engineering',
];

// Main Create/Edit Certificate Dialog Component
const CreateEditCertificateDialog = ({
  open,
  onClose,
  certificate = null,
  onSave,
  onBatchSave,
  isDarkMode,
}) => {
  const { user, isAdmin, isOffice } = useAuth();
  const { showNotification } = useNotification();
  const isEditing = !!certificate;
  const [currentTab, setCurrentTab] = useState(0);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importItemsDialogOpen, setImportItemsDialogOpen] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleOpenImportDialog = useCallback(() => {
    setImportDialogOpen(true);
  }, []);

  const handleCloseImportDialog = useCallback(() => {
    setImportDialogOpen(false);
  }, []);

  const handleOpenImportItemsDialog = useCallback(() => {
    setImportItemsDialogOpen(true);
  }, []);

  const handleCloseImportItemsDialog = useCallback(() => {
    setImportItemsDialogOpen(false);
  }, []);

  const handleImportData = useCallback(
    importedData => {
      // Populate the form with imported data
      setFormData(prev => ({
        ...prev,
        ...importedData,
        // Ensure Items array is properly set
        Items: importedData.Items || prev.Items,
      }));

      // Clear any existing errors since we're importing fresh data
      setErrors({});

      // Show success message
      showNotification('Certificate data imported successfully!', 'success');

      // Close import dialog
      setImportDialogOpen(false);
    },
    [showNotification]
  );

  const handleImportItems = useCallback(
    importedItems => {
      // Add imported items to existing items
      setFormData(prev => ({
        ...prev,
        Items: [...prev.Items, ...importedItems],
      }));

      // Clear any item-related errors
      setErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach(key => {
          if (key.startsWith('items.')) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });

      // Show success message
      showNotification(
        `Successfully imported ${importedItems.length} item(s)!`,
        'success'
      );

      // Close import items dialog
      setImportItemsDialogOpen(false);
    },
    [showNotification]
  );

  const [formData, setFormData] = useState({
    // Certificate Info
    // CocNumber: '',
    RegistrationNumber: '',
    ReferenceNumber: '',
    IssuerName: '',
    IssuingOffice: '',

    InvoiceValueFeeBasis: '',

    // Dates
    // IssuanceDate: new Date().toISOString().split('T')[0],
    //  ExpiryDate: '',
    AssessmentDate: new Date().toISOString().split('T')[0],

    // Importer Info
    ImporterName: '',
    ImporterAddress: 'N/A',
    ImporterCity: '',
    ImporterCountry: '',

    // Exporter Info
    ExporterName: '',
    ExporterAddress: '',
    ExporterCountry: '',

    // Invoice Info
    InvoiceNumber: '',
    InvoiceDate: '',
    InvoiceValue: '',
    InvoiceCurrency: 'USD',
    DeclaredValuePerContainer: '',
    Incoterms: '',

    // Shipment Info

    DocumentNumber: '',
    CountryOfShipment: '',
    DocumentDate: '',
    PointOfEntry: '',
    PackingDetails: '',
    ContainerDetails: '',
    NumberAndTypeOfPackage: '',
    ContainerSealNumber: '',
    Remarks: '',

    // NEW FIELDS FOR OFFICE ROLE
    ShipmentType: '',
    Branch: '',
    CountryOrigin: '',
    BlNo: '',
    BlDate: '',
    Signee: '',
    ContainersCount: '',

    // ADMIN ONLY FIELDS
    IsStage1Sync: false,
    IsStage2Sync: false,

    // Items
    Items: [
      {
        Type: '',
        ItemDescription: '',
        Quantity: '',
        GoodsValue: '',
        Currency: 'USD',
        Origin: '',
        StandardRefNumber: '',
        Brand: '',
        Packing: '',
        BatchNumber: '',
        SlotDate: '',
        ProductionDate: '',
        ExpirationDate: '',
      },
    ],
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [attachments, setAttachments] = useState([]);

  // Exporter autocomplete states
  const [exporterSearchTerm, setExporterSearchTerm] = useState('');
  const [exporterOptions, setExporterOptions] = useState([]);
  const [exporterLoading, setExporterLoading] = useState(false);
  const [selectedExporter, setSelectedExporter] = useState(null);

  // Debounced search term for exporter
  const debouncedExporterSearch = useDebounce(exporterSearchTerm, 500);

  // Search exporters when debounced term changes
  useEffect(() => {
    const searchExporters = async () => {
      if (debouncedExporterSearch.length < 2) {
        setExporterOptions([]);
        return;
      }

      setExporterLoading(true);
      try {
        const results = await certificatesService.searchExporterInformation(
          debouncedExporterSearch
        );
        setExporterOptions(Array.isArray(results) ? results : []);
      } catch (error) {
        console.error('Error searching exporters:', error);
        setExporterOptions([]);
      } finally {
        setExporterLoading(false);
      }
    };

    searchExporters();
  }, [debouncedExporterSearch]);

  const handleExporterInputChange = useCallback((event, newInputValue) => {
    console.log('handleExporterInputChange called with:', newInputValue);
    setExporterSearchTerm(newInputValue);

    // If user is typing and it's not an empty string, update the ExporterName
    if (newInputValue && newInputValue.trim() !== '') {
      setFormData(prev => ({
        ...prev,
        ExporterName: newInputValue.trim(),
      }));

      // Clear ExporterName error when user starts typing
      setErrors(prev => {
        if (prev.ExporterName) {
          const newErrors = { ...prev };
          delete newErrors.ExporterName;
          return newErrors;
        }
        return prev;
      });
    }
  }, []);

  // Handle exporter selection
  const handleExporterSelect = useCallback((event, value) => {
    console.log('handleExporterSelect called with:', {
      event,
      value,
      valueType: typeof value,
    });

    if (value && typeof value === 'object') {
      // Selected existing exporter
      console.log('Selected existing exporter:', value);
      setSelectedExporter(value);
      const exporterName = value.ExporterName || value.exporterName || '';
      const exporterAddress =
        value.ExporterAddress || value.exporterAddress || '';
      const exporterCountry =
        value.ExporterCountry || value.exporterCountry || '';

      setFormData(prev => ({
        ...prev,
        ExporterName: exporterName,
        ExporterAddress: exporterAddress,
        ExporterCountry: exporterCountry,
      }));

      console.log('Set formData with existing exporter:', {
        exporterName,
        exporterAddress,
        exporterCountry,
      });

      // Clear exporter-related errors when selecting existing exporter
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.ExporterName;
        delete newErrors.ExporterAddress;
        delete newErrors.ExporterCountry;
        return newErrors;
      });
    } else if (typeof value === 'string' && value.trim() !== '') {
      // User typed a new exporter name
      console.log('User typed new exporter name:', value);
      setSelectedExporter(null);
      setFormData(prev => ({
        ...prev,
        ExporterName: value.trim(),
      }));

      console.log('Set ExporterName to:', value.trim());

      // Clear only ExporterName error when typing
      setErrors(prev => {
        if (prev.ExporterName) {
          const newErrors = { ...prev };
          delete newErrors.ExporterName;
          return newErrors;
        }
        return prev;
      });
    } else if (
      value === null ||
      value === '' ||
      (typeof value === 'string' && value.trim() === '')
    ) {
      // Cleared selection or empty value
      console.log('Cleared exporter selection');
      setSelectedExporter(null);
      setFormData(prev => ({
        ...prev,
        ExporterName: '',
        ExporterAddress: '',
        ExporterCountry: '',
      }));
    }
  }, []);

  // Save new exporter when address or country changes (if it's a new exporter)
  const saveNewExporter = useCallback(async () => {
    if (
      !selectedExporter &&
      formData.ExporterName &&
      formData.ExporterAddress &&
      formData.ExporterCountry
    ) {
      try {
        const exporterData = {
          ExporterName: formData.ExporterName,
          ExporterAddress: formData.ExporterAddress,
          ExporterCountry: formData.ExporterCountry,
        };
        await certificatesService.saveExporterInformation(exporterData);
        console.log('New exporter saved:', exporterData);
      } catch (error) {
        console.error('Error saving new exporter:', error);
      }
    }
  }, [
    selectedExporter,
    formData.ExporterName,
    formData.ExporterAddress,
    formData.ExporterCountry,
  ]);

  // Input change handler
  const handleInputChange = useCallback(
    (field, value) => {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));

      // Clear error when user starts typing
      setErrors(prev => {
        if (prev[field]) {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        }
        return prev;
      });

      // If it's an exporter field and user is typing manually, clear selectedExporter
      if (
        ['ExporterName', 'ExporterAddress', 'ExporterCountry'].includes(field)
      ) {
        if (!selectedExporter) {
          // User is manually typing, this is fine
        } else {
          // User modified a field that was auto-filled, clear selection
          setSelectedExporter(null);
        }
      }
    },
    [selectedExporter]
  );

  // Initialize form data when editing
  useEffect(() => {
    if (isEditing && certificate) {
      setFormData({
        //   CocNumber: certificate.CocNumber || '',
        RegistrationNumber: certificate.RegistrationNumber || '',
        ReferenceNumber: certificate.ReferenceNumber || '',
        IssuerName: certificate.IssuerName || '',
        IssuingOffice: certificate.IssuingOffice || '',
        
        IssuanceDate: certificate.IssuanceDate
          ? certificate.IssuanceDate.split('T')[0]
          : '',
        ExpiryDate: certificate.ExpiryDate
          ? certificate.ExpiryDate.split('T')[0]
          : '',
           
        AssessmentDate: certificate.AssessmentDate
          ? certificate.AssessmentDate.split('T')[0]
          : '',

        ImporterName: certificate.ImporterName || '',
        ImporterAddress: certificate.ImporterAddress || '',
        ImporterCity: certificate.ImporterCity || '',
        ImporterCountry: certificate.ImporterCountry || '',

        ExporterName: certificate.ExporterName || '',
        ExporterAddress: certificate.ExporterAddress || '',
        ExporterCountry: certificate.ExporterCountry || '',

        InvoiceNumber: certificate.InvoiceNumber || '',
        InvoiceDate: certificate.InvoiceDate
          ? certificate.InvoiceDate.split('T')[0]
          : '',
        InvoiceValue: certificate.InvoiceValue || 0,
        InvoiceCurrency: certificate.InvoiceCurrency || 'USD',
        DeclaredValuePerContainer: certificate.DeclaredValuePerContainer || 0,
        Incoterms: certificate.Incoterms || '',

        ShipmentMethod: certificate.ShipmentType || '',
        DocumentNumber: certificate.DocumentNumber || '',
        CountryOfShipment: certificate.CountryOfShipment || '',
        DocumentDate: certificate.DocumentDate
          ? certificate.DocumentDate.split('T')[0]
          : '',
        PointOfEntry: certificate.PointOfEntry || '',
        PackingDetails: certificate.PackingDetails || '',
        ContainerDetails: certificate.ContainerDetails || '',
        NumberAndTypeOfPackage: certificate.NumberAndTypeOfPackage || '',
        ContainerSealNumber: certificate.ContainerSealNumber || '',
        Remarks: certificate.Remarks || '',

        // NEW FIELDS
        ShipmentType: certificate.ShipmentType || '',
        Branch: certificate.Branch || '',
        CountryOrigin: certificate.CountryOrigin || '',
        BlNo: certificate.BlNo || '',
        BlDate: certificate.BlDate ? certificate.BlDate.split('T')[0] : '',
        Signee: certificate.Signee || '',
        ContainersCount: certificate.ContainersCount || 0,
        InvoiceValueFeeBasis: certificate.InvoiceValueFeeBasis || 0,

        // ADMIN ONLY FIELDS
        IsStage1Sync: certificate.IsStage1Sync || false,
        IsStage2Sync: certificate.IsStage2Sync || false,

        Items:
          certificate.Items && certificate.Items.length > 0
            ? certificate.Items
            : [
                {
                  Type: '',
                  ItemDescription: '',
                  Quantity: '',
                  GoodsValue: 0,
                  Currency: 'USD',
                  Origin: '',
                  StandardRefNumber: '',
                  Brand: '',
                  Packing: '',
                  BatchNumber: '',
                  SlotDate: '',
                  ProductionDate: '',
                  ExpirationDate: '',
                },
              ],
      });

      // Set selected exporter for editing
      if (certificate.ExporterName) {
        setSelectedExporter({
          ExporterName: certificate.ExporterName,
          ExporterAddress: certificate.ExporterAddress,
          ExporterCountry: certificate.ExporterCountry,
        });
      }
    }
  }, [isEditing, certificate]);

  const handleItemUpdate = useCallback(
    (index, updatedItem, changedField = null) => {
      setFormData(prev => ({
        ...prev,
        Items: prev.Items.map((item, i) => (i === index ? updatedItem : item)),
      }));

      // Clear only the specific field error that was updated
      if (changedField) {
        setErrors(prev => {
          const newErrors = { ...prev };
          const errorKey = `items.${index}.${changedField}`;

          // Only clear the error for the specific field that changed
          if (newErrors[errorKey]) {
            delete newErrors[errorKey];
          }

          return newErrors;
        });
      }
    },
    []
  );

  const handleAddItem = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      Items: [
        ...prev.Items,
        {
          Type: '',
          ItemDescription: '',
          Quantity: '',
          GoodsValue: 0,
          Currency: 'USD',
          Origin: '',
          StandardRefNumber: '',
          Brand: '',
          Packing: '',
          BatchNumber: '',
          SlotDate: '',
          ProductionDate: '',
          ExpirationDate: '',
        },
      ],
    }));
  }, []);

  const handleRemoveItem = useCallback(
    index => {
      if (formData.Items.length > 1) {
        setFormData(prev => ({
          ...prev,
          Items: prev.Items.filter((_, i) => i !== index),
        }));
      }
    },
    [formData.Items.length]
  );

  const validateForm = useCallback(() => {
    const newErrors = {};

    // Required fields validation based on API requirements

    if (!formData.IssuerName) newErrors.IssuerName = 'Issuer name  is required';

    if (!formData.IssuingOffice)
      newErrors.IssuingOffice = 'Issuing Office is required';

    if (!formData.ImporterName)
      newErrors.ImporterName = 'Importer name is required';
    if (!formData.ImporterCity)
      newErrors.ImporterCity = 'Importer city is required';
    if (!formData.ImporterCountry)
      newErrors.ImporterCountry = 'Importer country is required';

    // Fixed: Proper validation for DeclaredValuePerContainer
    if (
      !formData.DeclaredValuePerContainer ||
      (typeof formData.DeclaredValuePerContainer === 'string' &&
        formData.DeclaredValuePerContainer.trim() === '') ||
      (typeof formData.DeclaredValuePerContainer === 'number' &&
        formData.DeclaredValuePerContainer <= 0)
    ) {
      newErrors.DeclaredValuePerContainer =
        'Declared Value Per Container is required and must be greater than 0';
    }

    if (
      !formData.InvoiceValueFeeBasis ||
      (typeof formData.InvoiceValueFeeBasis === 'string' &&
        formData.InvoiceValueFeeBasis.trim() === '') ||
      (typeof formData.InvoiceValueFeeBasis === 'number' &&
        formData.InvoiceValueFeeBasis <= 0)
    ) {
      newErrors.InvoiceValueFeeBasis =
        'Invoice Value (Fee Basis) is required and must be greater than 0';
    }

    // Exporter validation
    if (!formData.ExporterName || formData.ExporterName.trim() === '')
      newErrors.ExporterName = 'Exporter name is required';
    if (!formData.ExporterAddress || formData.ExporterAddress.trim() === '')
      newErrors.ExporterAddress = 'Exporter address is required';
    if (!formData.ExporterCountry || formData.ExporterCountry.trim() === '')
      newErrors.ExporterCountry = 'Exporter country is required';

    // Fixed: Proper validation for InvoiceValue
    if (
      !formData.InvoiceValue ||
      (typeof formData.InvoiceValue === 'string' &&
        formData.InvoiceValue.trim() === '') ||
      (typeof formData.InvoiceValue === 'number' && formData.InvoiceValue <= 0)
    ) {
      newErrors.InvoiceValue =
        'Invoice Value is required and must be greater than 0';
    }

    // Certificate info validation
    // COMMENTED OUT: Reference Number validation - Hidden from form
    // if (!formData.ReferenceNumber)
    //   newErrors.ReferenceNumber = 'Reference number is required';
    if (!formData.AssessmentDate)
      newErrors.AssessmentDate = 'Assessment date is required';

    // Invoice validation
    if (!formData.InvoiceNumber)
      newErrors.InvoiceNumber = 'Invoice number is required';
    if (!formData.InvoiceDate)
      newErrors.InvoiceDate = 'Invoice date is required';

    // Shipment validation
    if (!formData.ShipmentType)
      newErrors.ShipmentType = 'Shipment type is required';
    if (!formData.Branch) newErrors.Branch = 'Branch is required';
    if (!formData.CountryOrigin)
      newErrors.CountryOrigin = 'Country of origin is required';
    if (!formData.ContainersCount)
      newErrors.ContainersCount = 'Containers count is required';
    if (!formData.PointOfEntry)
      newErrors.PointOfEntry = 'Point of entry is required';
    if (!formData.PackingDetails)
      newErrors.PackingDetails = 'Packing details is required';
    if (!formData.ContainerDetails)
      newErrors.ContainerDetails = 'Container details is required';
    if (!formData.CountryOfShipment)
      newErrors.CountryOfShipment = 'Country of shipment is required';
    if (!formData.NumberAndTypeOfPackage)
      newErrors.NumberAndTypeOfPackage =
        'Number and type of package is required';

    if (formData.Items && Array.isArray(formData.Items)) {
      formData.Items.forEach((item, index) => {
        if (
          !item.Type ||
          (typeof item.Type === 'string' && item.Type.trim() === '')
        )
          newErrors[`items.${index}.Type`] = 'Item type is required';
        // String field validation with proper null checks and trim()
        if (
          !item.ItemDescription ||
          (typeof item.ItemDescription === 'string' &&
            item.ItemDescription.trim() === '')
        )
          newErrors[`items.${index}.ItemDescription`] =
            'Item description is required';

        if (
          !item.Quantity ||
          (typeof item.Quantity === 'string' && item.Quantity.trim() === '')
        )
          newErrors[`items.${index}.Quantity`] = 'Quantity is required';

        if (
          !item.Packing ||
          (typeof item.Packing === 'string' && item.Packing.trim() === '')
        )
          newErrors[`items.${index}.Packing`] = 'Packing Method is required';

        if (
          !item.Origin ||
          (typeof item.Origin === 'string' && item.Origin.trim() === '')
        )
          newErrors[`items.${index}.Origin`] = 'Origin is required';

        if (
          !item.StandardRefNumber ||
          (typeof item.StandardRefNumber === 'string' &&
            item.StandardRefNumber.trim() === '')
        )
          newErrors[`items.${index}.StandardRefNumber`] =
            'Standard reference number is required';

        // Number field validation with proper conversion and checks
        const goodsValue =
          typeof item.GoodsValue === 'number'
            ? item.GoodsValue
            : parseFloat(item.GoodsValue);

        if (!item.GoodsValue || isNaN(goodsValue) || goodsValue <= 0)
          newErrors[`items.${index}.GoodsValue`] =
            'Goods value is required and must be greater than 0';

        if (
          !item.Currency ||
          (typeof item.Currency === 'string' && item.Currency.trim() === '')
        )
          newErrors[`items.${index}.Currency`] = 'Currency is required';
      });
    } else if (!formData.Items || formData.Items.length === 0) {
      // Handle case where no items exist
      newErrors.Items = 'At least one item is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    console.log('Starting form submission...');
    console.log('Current formData:', {
      ExporterName: formData.ExporterName,
      ExporterAddress: formData.ExporterAddress,
      ExporterCountry: formData.ExporterCountry,
    });

    if (!validateForm()) {
      showNotification('Please fix the validation errors', 'error');
      console.log('Validation failed with errors:', errors);
      return;
    }

    setIsLoading(true);
    try {
      // Check if we need to save a new exporter
      if (
        !selectedExporter &&
        formData.ExporterName &&
        formData.ExporterAddress &&
        formData.ExporterCountry
      ) {
        console.log('Saving new exporter...');
        try {
          const exporterData = {
            ExporterName: formData.ExporterName.trim(),
            ExporterAddress: formData.ExporterAddress.trim(),
            ExporterCountry: formData.ExporterCountry.trim(),
          };

          await certificatesService.saveExporterInformation(exporterData);
          console.log('New exporter saved successfully:', exporterData);
          showNotification('New exporter information saved', 'success');
        } catch (exporterError) {
          console.error('Error saving new exporter:', exporterError);
          // Don't fail the entire operation if exporter save fails
          showNotification(
            'Warning: Could not save exporter information for future use',
            'warning'
          );
        }
      }

      // Prepare data for API
      const submitData = {
        ...formData,
        // Ensure exporter fields are trimmed
        ExporterName: formData.ExporterName?.trim() || '',
        ExporterAddress: formData.ExporterAddress?.trim() || '',
        ExporterCountry: formData.ExporterCountry?.trim() || '',
        // Convert dates to ISO format
        AssessmentDate: new Date(formData.AssessmentDate).toISOString(),
        InvoiceDate: new Date(formData.InvoiceDate).toISOString(),
        DocumentDate: formData.DocumentDate
          ? new Date(formData.DocumentDate).toISOString()
          : null,
        BlDate: formData.BlDate
          ? new Date(formData.BlDate).toISOString()
          : null,
        // Include IssuanceDate and ExpiryDate only when editing
        ...(isEditing && formData.IssuanceDate && {
          IssuanceDate: new Date(formData.IssuanceDate).toISOString()
        }),
        ...(isEditing && formData.ExpiryDate && {
          ExpiryDate: new Date(formData.ExpiryDate).toISOString()
        }),
      };

      console.log('Submitting certificate with data:', submitData);

      // Save certificate
      const savedCertificate = await onSave(
        submitData,
        isEditing ? certificate.Id : null
      );

      // Get certificate ID for attachments
      const certificateId = isEditing
        ? certificate.Id
        : savedCertificate?.id || savedCertificate?.Id;

      // Upload attachments if any and not editing
      if (!isEditing && attachments.length > 0 && certificateId) {
        try {
          for (const attachment of attachments) {
            if (attachment.isNew && attachment.file) {
              const attachmentData = {
                fileName: attachment.fileName,
                file: attachment.file,
                description: attachment.description || 'Supporting document',
              };
              await certificatesService.addAttachment(
                certificateId,
                attachmentData
              );
            }
          }
          showNotification(
            `Certificate ${isEditing ? 'updated' : 'created'} successfully with ${attachments.length} attachment(s)`,
            'success'
          );
        } catch (attachmentError) {
          console.error('Error uploading attachments:', attachmentError);
          showNotification(
            `Certificate ${isEditing ? 'updated' : 'created'} successfully, but some attachments failed to upload`,
            'warning'
          );
        }
      } else {
        showNotification(
          `Certificate ${isEditing ? 'updated' : 'created'} successfully`,
          'success'
        );
      }

      onClose();
    } catch (error) {
      console.error('Submit error:', error);

      // Handle specific validation errors from API
      if (error.response?.status === 400 && error.response?.data?.errors) {
        const apiErrors = error.response.data.errors;
        const newErrors = {};

        // Map API errors to form errors
        Object.keys(apiErrors).forEach(field => {
          newErrors[field] = apiErrors[field][0]; // Take first error message
        });

        setErrors(prev => ({ ...prev, ...newErrors }));
        showNotification('Please fix the validation errors', 'error');
      } else {
        showNotification(
          `Failed to ${isEditing ? 'update' : 'create'} certificate: ${error.message || 'Unknown error'}`,
          'error'
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    validateForm,
    formData,
    selectedExporter,
    onSave,
    isEditing,
    certificate,
    attachments,
    onClose,
    showNotification,
    errors,
  ]);

  const handleAttemptClose = useCallback(() => {
    setShowConfirmDialog(true);
  }, []);

  const handleConfirmClose = useCallback(() => {
    setFormData({
      //   CocNumber: '',
      RegistrationNumber: '',
      ReferenceNumber: '',
      IssuerName: '',
      IssuingOffice: '',
      //  IssuanceDate: new Date().toISOString().split('T')[0],
      //   ExpiryDate: '',
      AssessmentDate: new Date().toISOString().split('T')[0],
      ImporterName: '',
      ImporterAddress: '',
      ImporterCity: '',
      ImporterCountry: '',
      ExporterName: '',
      ExporterAddress: '',
      ExporterCountry: '',
      InvoiceNumber: '',
      InvoiceDate: '',
      InvoiceValue: '',
      InvoiceCurrency: 'USD',
      DeclaredValuePerContainer: '',
      Incoterms: '',
      DocumentNumber: '',
      CountryOfShipment: '',
      DocumentDate: '',
      PointOfEntry: '',
      PackingDetails: '',
      ContainerDetails: '',
      NumberAndTypeOfPackage: '',
      ContainerSealNumber: '',
      Remarks: '',
      ShipmentType: '',
      ShipmentMethod: '',
      Branch: '',
      CountryOrigin: '',
      BlNo: '',
      BlDate: '',
      Signee: '',
      ContainersCount: '',
      IsStage1Sync: false,
      IsStage2Sync: false,
      Items: [
        {
          ItemDescription: '',
          Quantity: '',
          GoodsValue: '',
          Currency: 'USD',
          Origin: '',
          StandardRefNumber: '',
          Brand: '',
          Packing: '',
          BatchNumber: '',
          SlotDate: '',
          ProductionDate: '',
          ExpirationDate: '',
        },
      ],
    });
    setErrors({});
    setAttachments([]);
    setSelectedExporter(null);
    setExporterSearchTerm('');
    setExporterOptions([]);
    setShowConfirmDialog(false);
    onClose();
  }, [onClose]);

  const handleCancelClose = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleAttemptClose}
        maxWidth="xl"
        fullWidth
        fullScreen={window.innerWidth < 768} // Full screen on mobile
        PaperProps={{
          sx: {
            background: isDarkMode
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            border: isDarkMode
              ? '1px solid rgba(148, 163, 184, 0.1)'
              : '1px solid rgba(148, 163, 184, 0.2)',
            borderRadius: window.innerWidth < 768 ? 0 : 3, // No border radius on mobile
            maxHeight: window.innerWidth < 768 ? '100vh' : '95vh',
            margin: window.innerWidth < 768 ? 0 : '32px',
          },
        }}
      >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isEditing ? (
              <Edit size={24} color="#8b5cf6" />
            ) : (
              <Plus size={24} color="#3b82f6" />
            )}
            <Typography
              variant="h6"
              sx={{
                ml: 1,
                fontWeight: 600,
                color: isDarkMode ? '#f1f5f9' : '#1e293b',
              }}
            >
              {isEditing
                ? `Edit Certificate - ${certificate?.CocNumber}`
                : 'Create New Certificate'}
            </Typography>
          </Box>

          {/* ADD THIS: Import Button for new certificates only */}
          {!isEditing && (
            <CustomButton
              variant="outlined"
              startIcon={<Upload size={16} />}
              onClick={handleOpenImportDialog}
              sx={{
                borderColor: '#10b981',
                color: '#10b981',
                '&:hover': {
                  background: 'rgba(16, 185, 129, 0.1)',
                  borderColor: '#059669',
                },
              }}
            >
              Import from Excel/CSV
            </CustomButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: window.innerWidth < 768 ? 'calc(100vh - 120px)' : 'auto' }}>
        <Box sx={{ 
          p: { xs: 2, sm: 3 }, // Responsive padding
          maxHeight: window.innerWidth < 768 ? 'calc(100vh - 140px)' : '75vh', 
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: isDarkMode ? 'rgba(30, 41, 59, 0.3)' : 'rgba(0, 0, 0, 0.1)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: isDarkMode ? 'rgba(148, 163, 184, 0.5)' : 'rgba(0, 0, 0, 0.3)',
            borderRadius: '3px',
          },
        }}>
          {/* ENHANCED Certificate Information Section */}
          <Card
            sx={{
              mb: 4,
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(168, 85, 247, 0.15) 100%)'
                : 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(168, 85, 247, 0.08) 100%)',
              border: '2px solid rgba(99, 102, 241, 0.3)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(99, 102, 241, 0.2)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h5"
                sx={{
                  color: isDarkMode ? '#f1f5f9' : '#1e293b',
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 700,
                }}
              >
                <FileText
                  size={24}
                  style={{ marginRight: 12, color: '#6366f1' }}
                />
                📋 Certificate Information
                <Chip
                  label="BASIC INFO"
                  size="small"
                  sx={{
                    ml: 2,
                    background:
                      'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              </Typography>

              <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12} sm={6} lg={4}>
                  <TextField
                    label="📄 Registration Number"
                    fullWidth
                    value={formData.RegistrationNumber}
                    onChange={e =>
                      handleInputChange('RegistrationNumber', e.target.value)
                    }
                    error={!!errors.RegistrationNumber}
                    helperText={errors.RegistrationNumber}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDarkMode
                          ? 'rgba(99, 102, 241, 0.05)'
                          : 'rgba(99, 102, 241, 0.02)',
                      },
                    }}
                  />
                </Grid>

                {/* COMMENTED OUT: Reference Number - Hidden from form, can be restored later */}
                {/* <Grid item xs={12} sm={6} lg={4}>
                  <TextField
                    label="🔗 Reference Number"
                    fullWidth
                    value={formData.ReferenceNumber}
                    onChange={e =>
                      handleInputChange('ReferenceNumber', e.target.value)
                    }
                    error={!!errors.ReferenceNumber}
                    helperText={errors.ReferenceNumber}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDarkMode
                          ? 'rgba(99, 102, 241, 0.05)'
                          : 'rgba(99, 102, 241, 0.02)',
                      },
                    }}
                  />
                </Grid> */}

                <Grid item xs={12} sm={6} lg={4}>
                  <TextField
                    label="👤 Issuer Name"
                    fullWidth
                    value={formData.IssuerName}
                    onChange={e =>
                      handleInputChange('IssuerName', e.target.value)
                    }
                    error={!!errors.IssuerName}
                    helperText={errors.IssuerName}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDarkMode
                          ? 'rgba(99, 102, 241, 0.05)'
                          : 'rgba(99, 102, 241, 0.02)',
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} lg={4}>
                  <TextField
                    label="🏢 Issuing Office"
                    fullWidth
                    value={formData.IssuingOffice}
                    onChange={e =>
                      handleInputChange('IssuingOffice', e.target.value)
                    }
                    error={!!errors.IssuingOffice}
                    helperText={errors.IssuingOffice}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDarkMode
                          ? 'rgba(99, 102, 241, 0.05)'
                          : 'rgba(99, 102, 241, 0.02)',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Dates Section */}
          <Typography
            variant="h6"
            sx={{
              color: isDarkMode ? '#f1f5f9' : '#1e293b',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Calendar size={20} style={{ marginRight: 8 }} />
            Important Dates
          </Typography>
          
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
            {/* Only show IssuanceDate and ExpiryDate when editing */}
            {isEditing && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Issuance Date"
                    type="date"
                    fullWidth
                    value={formData.IssuanceDate || ''}
                    onChange={e =>
                      handleInputChange('IssuanceDate', e.target.value)
                    }
                    error={!!errors.IssuanceDate}
                    helperText={errors.IssuanceDate || 'Certificate issuance date'}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDarkMode
                          ? 'rgba(245, 158, 11, 0.05)'
                          : 'rgba(245, 158, 11, 0.02)',
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Expiry Date"
                    type="date"
                    fullWidth
                    value={formData.ExpiryDate || ''}
                    onChange={e => handleInputChange('ExpiryDate', e.target.value)}
                    error={!!errors.ExpiryDate}
                    helperText={errors.ExpiryDate || 'Certificate expiry date'}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDarkMode
                          ? 'rgba(245, 158, 11, 0.05)'
                          : 'rgba(245, 158, 11, 0.02)',
                      },
                    }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Assessment Date *"
                type="date"
                fullWidth
                value={formData.AssessmentDate}
                onChange={e =>
                  handleInputChange('AssessmentDate', e.target.value)
                }
                error={!!errors.AssessmentDate}
                helperText={errors.AssessmentDate}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="warning">
                <strong>Note:</strong> Assessment Date should be 3-4 days before
                Issuance Date
              </Alert>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Importer Information */}
          <Typography
            variant="h6"
            sx={{
              color: isDarkMode ? '#f1f5f9' : '#1e293b',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <User size={20} style={{ marginRight: 8 }} />
            Importer Information
          </Typography>

          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Importer Name *"
                fullWidth
                value={formData.ImporterName}
                onChange={e =>
                  handleInputChange('ImporterName', e.target.value)
                }
                error={!!errors.ImporterName}
                helperText={errors.ImporterName}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Importer City"
                fullWidth
                value={formData.ImporterCity}
                onChange={e =>
                  handleInputChange('ImporterCity', e.target.value)
                }
                error={!!errors.ImporterCity}
                helperText={errors.ImporterCity}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Importer Address"
                fullWidth
                multiline
                rows={2}
                value={formData.ImporterAddress}
                onChange={e =>
                  handleInputChange('ImporterAddress', e.target.value)
                }
                error={!!errors.ImporterAddress}
                helperText={errors.ImporterAddress}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Importer Country"
                fullWidth
                value={formData.ImporterCountry}
                onChange={e =>
                  handleInputChange('ImporterCountry', e.target.value)
                }
                error={!!errors.ImporterCountry}
                helperText={errors.ImporterCountry}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* ENHANCED Exporter Information with Autocomplete */}
          <Card
            sx={{
              mb: 4,
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)'
                : 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.08) 100%)',
              border: '2px solid rgba(16, 185, 129, 0.3)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(16, 185, 129, 0.2)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h5"
                sx={{
                  color: isDarkMode ? '#f1f5f9' : '#1e293b',
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 700,
                }}
              >
                <Building
                  size={24}
                  style={{ marginRight: 12, color: '#10b981' }}
                />
                🏭 Exporter Information
                <Chip
                  label="SMART SEARCH"
                  size="small"
                  sx={{
                    ml: 2,
                    background:
                      'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              </Typography>

              <Alert
                severity="info"
                sx={{
                  mb: 3,
                  background: isDarkMode
                    ? 'rgba(16, 185, 129, 0.1)'
                    : 'rgba(16, 185, 129, 0.05)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                }}
              >
                <Typography variant="body2">
                  <strong>Smart Search:</strong> Start typing an exporter name
                  to search existing records. Address and country will auto-fill
                  when you select an existing exporter.
                </Typography>
              </Alert>

              <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12}>
                  <Autocomplete
                    freeSolo
                    options={exporterOptions}
                    getOptionLabel={option => {
                      if (typeof option === 'string') return option;
                      return option.ExporterName || option.exporterName || '';
                    }}
                    value={selectedExporter || formData.ExporterName || ''}
                    inputValue={exporterSearchTerm}
                    onInputChange={handleExporterInputChange}
                    onChange={handleExporterSelect}
                    loading={exporterLoading}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="🏭 Exporter Name *"
                        fullWidth
                        multiline
                        rows={2}
                        required
                        error={!!errors.ExporterName}
                        helperText={
                          errors.ExporterName ||
                          (selectedExporter
                            ? 'Selected from existing records'
                            : 'Type to search existing exporters or enter new name')
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {exporterLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: isDarkMode
                              ? 'rgba(16, 185, 129, 0.05)'
                              : 'rgba(16, 185, 129, 0.02)',
                            borderColor: errors.ExporterName
                              ? '#ef4444'
                              : undefined,
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {option.ExporterName || option.exporterName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.ExporterCountry || option.exporterCountry} •{' '}
                            {option.ExporterAddress || option.exporterAddress}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="🏠 Exporter Address *"
                    fullWidth
                    required
                    multiline
                    rows={2}
                    value={formData.ExporterAddress}
                    onChange={e =>
                      handleInputChange('ExporterAddress', e.target.value)
                    }
                    error={!!errors.ExporterAddress}
                    helperText={
                      errors.ExporterAddress ||
                      (selectedExporter
                        ? 'Auto-filled from selected exporter'
                        : 'Enter exporter address')
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDarkMode
                          ? 'rgba(16, 185, 129, 0.05)'
                          : 'rgba(16, 185, 129, 0.02)',
                        borderColor: errors.ExporterAddress
                          ? '#ef4444'
                          : undefined,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="🌍 Exporter Country *"
                    fullWidth
                    required
                    value={formData.ExporterCountry}
                    onChange={e =>
                      handleInputChange('ExporterCountry', e.target.value)
                    }
                    error={!!errors.ExporterCountry}
                    helperText={
                      errors.ExporterCountry ||
                      (selectedExporter
                        ? 'Auto-filled from selected exporter'
                        : 'Enter exporter country')
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDarkMode
                          ? 'rgba(16, 185, 129, 0.05)'
                          : 'rgba(16, 185, 129, 0.02)',
                        borderColor: errors.ExporterCountry
                          ? '#ef4444'
                          : undefined,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Divider sx={{ my: 3 }} />

          {/* Invoice Information */}
          <Typography
            variant="h6"
            sx={{
              color: isDarkMode ? '#f1f5f9' : '#1e293b',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <DollarSign size={20} style={{ marginRight: 8 }} />
            Invoice Information
          </Typography>

          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Invoice Number *"
                fullWidth
                value={formData.InvoiceNumber}
                onChange={e =>
                  handleInputChange('InvoiceNumber', e.target.value)
                }
                error={!!errors.InvoiceNumber}
                helperText={errors.InvoiceNumber}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Invoice Date *"
                type="date"
                fullWidth
                value={formData.InvoiceDate}
                onChange={e => handleInputChange('InvoiceDate', e.target.value)}
                error={!!errors.InvoiceDate}
                helperText={errors.InvoiceDate}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Invoice Value *"
                type="number"
                fullWidth
                value={formData.InvoiceValue}
                onChange={e =>
                  handleInputChange('InvoiceValue', e.target.value)
                }
                error={!!errors.InvoiceValue}
                helperText={errors.InvoiceValue}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth error={!!errors.InvoiceCurrency}>
                <InputLabel>Invoice Currency</InputLabel>
                <Select
                  value={formData.InvoiceCurrency}
                  onChange={e =>
                    handleInputChange('InvoiceCurrency', e.target.value)
                  }
                  label="Invoice Currency"
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="IQD">IQD</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="AED">AED</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth error={!!errors.Incoterms}>
                <InputLabel>Incoterms </InputLabel>
                <Select
                  value={formData.Incoterms}
                  onChange={e => handleInputChange('Incoterms', e.target.value)}
                  label="Incoterms "
                >
                  <MenuItem value="FOB">FOB – Free On Board</MenuItem>
                  <MenuItem value="CFR">CFR – Cost and Freight</MenuItem>
                  <MenuItem value="CIF">
                    CIF – Cost, Insurance and Freight
                  </MenuItem>
                  <MenuItem value="FAS">FAS – Free Alongside Ship</MenuItem>
                  <MenuItem value="EXW">EXW – Ex Works</MenuItem>
                  <MenuItem value="FCA">FCA – Free Carrier</MenuItem>
                  <MenuItem value="CPT">CPT – Carriage Paid To</MenuItem>
                  <MenuItem value="CIP">
                    CIP – Carriage and Insurance Paid To
                  </MenuItem>
                  <MenuItem value="DAP">DAP – Delivered at Place</MenuItem>
                  <MenuItem value="DPU">
                    DPU – Delivered at Place Unloaded
                  </MenuItem>
                  <MenuItem value="DDP">DDP – Delivered Duty Paid</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Invoice Value (Fee Basis)"
                type="number"
                fullWidth
                value={formData.InvoiceValueFeeBasis}
                onChange={e =>
                  handleInputChange('InvoiceValueFeeBasis', e.target.value)
                }
                error={!!errors.InvoiceValueFeeBasis}
                helperText={errors.InvoiceValueFeeBasis}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Declared Value Per Container (FOB)"
                type="number"
                fullWidth
                value={formData.DeclaredValuePerContainer}
                onChange={e =>
                  handleInputChange('DeclaredValuePerContainer', e.target.value)
                }
                error={!!errors.DeclaredValuePerContainer}
                helperText={errors.DeclaredValuePerContainer}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Shipment & Origin Details Section */}
          <Card
            sx={{
              mb: 4,
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)'
                : 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(59, 130, 246, 0.2)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h5"
                sx={{
                  color: isDarkMode ? '#f1f5f9' : '#1e293b',
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 700,
                }}
              >
                <Ship size={24} style={{ marginRight: 12, color: '#3b82f6' }} />
                🚢 Shipment & Origin Details
                <Chip
                  label="REQUIRED"
                  size="small"
                  sx={{
                    ml: 2,
                    background:
                      'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              </Typography>

              <Grid container spacing={{ xs: 2, sm: 3 }}>
                {/* Shipment Type */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.ShipmentType}>
                    <InputLabel>🚛 Shipment Type *</InputLabel>
                    <Select
                      value={formData.ShipmentType}
                      onChange={e => {
                        handleInputChange('ShipmentType', e.target.value);
                        handleInputChange('ShipmentMethod', e.target.value);
                      }}
                      label="🚛 Shipment Type *"
                    >
                      <MenuItem value="Sea">🚢 Sea Freight</MenuItem>
                      <MenuItem value="Air">✈️ Air Freight</MenuItem>
                      <MenuItem value="Land">🚛 Land Transport</MenuItem>
                    </Select>
                    {errors.ShipmentType && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 1, ml: 1 }}
                      >
                        {errors.ShipmentType}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {/* Containers Count */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="📦 Containers Count *"
                    type="number"
                    fullWidth
                    value={formData.ContainersCount}
                    onChange={e =>
                      handleInputChange('ContainersCount', e.target.value)
                    }
                    error={!!errors.ContainersCount}
                    helperText={errors.ContainersCount}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDarkMode
                          ? 'rgba(59, 130, 246, 0.05)'
                          : 'rgba(59, 130, 246, 0.02)',
                      },
                    }}
                  />
                </Grid>

                {/* Branch */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="🏢 Branch *"
                    fullWidth
                    value={formData.Branch}
                    onChange={e => handleInputChange('Branch', e.target.value)}
                    error={!!errors.Branch}
                    helperText={errors.Branch}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDarkMode
                          ? 'rgba(59, 130, 246, 0.05)'
                          : 'rgba(59, 130, 246, 0.02)',
                      },
                    }}
                  />
                </Grid>

                {/* Country of Origin */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="🌍 Country of Origin *"
                    fullWidth
                    value={formData.CountryOrigin}
                    onChange={e =>
                      handleInputChange('CountryOrigin', e.target.value)
                    }
                    error={!!errors.CountryOrigin}
                    helperText={errors.CountryOrigin}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDarkMode
                          ? 'rgba(59, 130, 246, 0.05)'
                          : 'rgba(59, 130, 246, 0.02)',
                      },
                    }}
                  />
                </Grid>

                {/* BL Number */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="📋 BL Number"
                    fullWidth
                    value={formData.BlNo}
                    onChange={e => handleInputChange('BlNo', e.target.value)}
                    error={!!errors.BlNo}
                    helperText={errors.BlNo || 'Bill of Lading Number'}
                    placeholder="Enter Bill of Lading Number"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDarkMode
                          ? 'rgba(59, 130, 246, 0.05)'
                          : 'rgba(59, 130, 246, 0.02)',
                      },
                    }}
                  />
                </Grid>

                {/* BL Date */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="📅 BL Date"
                    type="date"
                    fullWidth
                    value={formData.BlDate}
                    onChange={e => handleInputChange('BlDate', e.target.value)}
                    error={!!errors.BlDate}
                    helperText={errors.BlDate}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDarkMode
                          ? 'rgba(59, 130, 246, 0.05)'
                          : 'rgba(59, 130, 246, 0.02)',
                      },
                    }}
                  />
                </Grid>

                {/* Signee */}
                <Grid item xs={12}>
                  <TextField
                    label="✍️ Signee"
                    fullWidth
                    value={formData.Signee}
                    onChange={e => handleInputChange('Signee', e.target.value)}
                    error={!!errors.Signee}
                    helperText={errors.Signee || 'Certificate signee name'}
                    placeholder="Certificate signee name"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isDarkMode
                          ? 'rgba(59, 130, 246, 0.05)'
                          : 'rgba(59, 130, 246, 0.02)',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Divider sx={{ my: 3 }} />

          {/* Additional Shipment Information */}
          <Typography
            variant="h6"
            sx={{
              color: isDarkMode ? '#f1f5f9' : '#1e293b',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Truck size={20} style={{ marginRight: 8 }} />
            Additional Shipment Information
          </Typography>

          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Document Number"
                fullWidth
                value={formData.DocumentNumber}
                onChange={e =>
                  handleInputChange('DocumentNumber', e.target.value)
                }
                error={!!errors.DocumentNumber}
                helperText={errors.DocumentNumber}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Country of Shipment"
                fullWidth
                value={formData.CountryOfShipment}
                onChange={e =>
                  handleInputChange('CountryOfShipment', e.target.value)
                }
                error={!!errors.CountryOfShipment}
                helperText={errors.CountryOfShipment}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Document Date"
                type="date"
                fullWidth
                value={formData.DocumentDate}
                onChange={e =>
                  handleInputChange('DocumentDate', e.target.value)
                }
                error={!!errors.DocumentDate}
                helperText={errors.DocumentDate}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Point of Entry"
                fullWidth
                value={formData.PointOfEntry}
                onChange={e =>
                  handleInputChange('PointOfEntry', e.target.value)
                }
                error={!!errors.PointOfEntry}
              >
                {pointOfEntryOptions.map((entry, index) => (
                  <MenuItem key={index} value={entry}>
                    {entry}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Container Seal Number"
                fullWidth
                value={formData.ContainerSealNumber}
                onChange={e =>
                  handleInputChange('ContainerSealNumber', e.target.value)
                }
                error={!!errors.ContainerSealNumber}
                helperText={errors.ContainerSealNumber}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Packing Details"
                fullWidth
                value={formData.PackingDetails}
                onChange={e =>
                  handleInputChange('PackingDetails', e.target.value)
                }
                error={!!errors.PackingDetails}
                helperText={errors.PackingDetails}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Container Details"
                fullWidth
                value={formData.ContainerDetails}
                onChange={e =>
                  handleInputChange('ContainerDetails', e.target.value)
                }
                error={!!errors.ContainerDetails}
                helperText={errors.ContainerDetails}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Number and Type of Package"
                fullWidth
                value={formData.NumberAndTypeOfPackage}
                onChange={e =>
                  handleInputChange('NumberAndTypeOfPackage', e.target.value)
                }
                error={!!errors.NumberAndTypeOfPackage}
                helperText={errors.NumberAndTypeOfPackage}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Remarks"
                fullWidth
                multiline
                rows={3}
                value={formData.Remarks}
                onChange={e => handleInputChange('Remarks', e.target.value)}
                error={!!errors.Remarks}
                helperText={errors.Remarks}
              />
            </Grid>
          </Grid>

          {/* ADMIN ONLY SECTION */}
          {isAdmin() && isEditing && (
            <>
              <Divider sx={{ my: 3 }} />

              <Typography
                variant="h6"
                sx={{
                  color: isDarkMode ? '#f1f5f9' : '#1e293b',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <FileSignature size={20} style={{ marginRight: 8 }} />
                Sync Status (Admin Only)
              </Typography>

              <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography
                      variant="body1"
                      sx={{ color: isDarkMode ? '#e2e8f0' : '#1e293b' }}
                    >
                      Stage 1 Sync:
                    </Typography>
                    <Chip
                      label={formData.IsStage1Sync ? 'Synced' : 'Not Synced'}
                      size="small"
                      sx={{
                        background: formData.IsStage1Sync
                          ? 'linear-gradient(135deg, #10b98120, #10b98110)'
                          : 'linear-gradient(135deg, #ef444420, #ef444410)',
                        color: formData.IsStage1Sync ? '#10b981' : '#ef4444',
                        border: `1px solid ${formData.IsStage1Sync ? '#10b98130' : '#ef444430'}`,
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography
                      variant="body1"
                      sx={{ color: isDarkMode ? '#e2e8f0' : '#1e293b' }}
                    >
                      Stage 2 Sync:
                    </Typography>
                    <Chip
                      label={formData.IsStage2Sync ? 'Synced' : 'Not Synced'}
                      size="small"
                      sx={{
                        background: formData.IsStage2Sync
                          ? 'linear-gradient(135deg, #10b98120, #10b98110)'
                          : 'linear-gradient(135deg, #ef444420, #ef444410)',
                        color: formData.IsStage2Sync ? '#10b981' : '#ef4444',
                        border: `1px solid ${formData.IsStage2Sync ? '#10b98130' : '#ef444430'}`,
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Certificate Items */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: isDarkMode ? '#f1f5f9' : '#1e293b',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Package size={20} style={{ marginRight: 8 }} />
              Certificate Items
            </Typography>

            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 1, sm: 2 },
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' }
            }}>
              <CustomButton
                variant="outlined"
                onClick={handleOpenImportItemsDialog}
                startIcon={<Upload size={16} />}
                sx={{
                  borderColor: '#10b981',
                  color: '#10b981',
                  '&:hover': {
                    background: 'rgba(16, 185, 129, 0.1)',
                    borderColor: '#059669',
                  },
                }}
              >
                Import Items
              </CustomButton>
              <CustomButton
                variant="outlined"
                onClick={handleAddItem}
                startIcon={<Plus size={16} />}
                sx={{
                  borderColor: '#3b82f6',
                  color: '#3b82f6',
                  '&:hover': {
                    background: 'rgba(59, 130, 246, 0.1)',
                  },
                }}
              >
                Add Item
              </CustomButton>
            </Box>
          </Box>

          {/* Show validation alert if there are item errors */}
          {Object.keys(errors).some(key => key.startsWith('items.')) && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Please fix the validation errors in the certificate items below.
              </Typography>
            </Alert>
          )}

          {formData.Items.map((item, index) => (
            <CertificateItemForm
              key={index}
              item={item}
              index={index}
              onUpdate={handleItemUpdate}
              onRemove={handleRemoveItem}
              isDarkMode={isDarkMode}
              errors={errors}
            />
          ))}

          <Divider sx={{ my: 3 }} />

          {/* Incomplete Fields Warning Section - Final Review */}
          <IncompleteFieldsWarning
            formData={formData}
            isDarkMode={isDarkMode}
          />

          {/* Import Certificate Dialog */}
          <ImportCertificateDialog
            open={importDialogOpen}
            onClose={handleCloseImportDialog}
            onImport={handleImportData}
            isDarkMode={isDarkMode}
          />

          {/* Import Items Dialog */}
          <ImportItemsDialog
            open={importItemsDialogOpen}
            onClose={handleCloseImportItemsDialog}
            onImport={handleImportItems}
            isDarkMode={isDarkMode}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: { xs: 2, sm: 3 },
          borderTop: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 1 },
        }}
      >
        <CustomButton
          variant="outlined"
          onClick={handleAttemptClose}
          disabled={isLoading}
          fullWidth={{ xs: true, sm: false }}
        >
          Cancel
        </CustomButton>

        <CustomButton
          variant="gradient"
          gradient={isEditing ? 'secondary' : 'primary'}
          onClick={handleSubmit}
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : isEditing ? (
              <Save size={16} />
            ) : (
              <Plus size={16} />
            )
          }
          fullWidth={{ xs: true, sm: false }}
          sx={{
            background: isEditing
              ? 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)'
              : 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            boxShadow: isEditing
              ? '0 4px 15px rgba(139, 92, 246, 0.4)'
              : '0 4px 15px rgba(59, 130, 246, 0.4)',
            '&:hover': {
              background: isEditing
                ? 'linear-gradient(135deg, #7c3aed 0%, #db2777 100%)'
                : 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
              boxShadow: isEditing
                ? '0 8px 25px rgba(139, 92, 246, 0.6)'
                : '0 8px 25px rgba(59, 130, 246, 0.6)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          {isLoading
            ? isEditing
              ? 'Updating...'
              : 'Creating...'
            : isEditing
              ? 'Update Certificate'
              : 'Create Certificate'}
        </CustomButton>
      </DialogActions>
    </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: isDarkMode
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            border: isDarkMode
              ? '1px solid rgba(148, 163, 184, 0.1)'
              : '1px solid rgba(148, 163, 184, 0.2)',
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AlertTriangle size={24} color="#ef4444" />
            <Typography
              variant="h6"
              sx={{
                color: isDarkMode ? '#f1f5f9' : '#1e293b',
                fontWeight: 600,
              }}
            >
              Discard Changes?
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body1"
            sx={{
              color: isDarkMode ? '#cbd5e1' : '#475569',
              mb: 2,
            }}
          >
            Are you sure you want to close this form? All unsaved changes will be
            lost.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            borderTop: isDarkMode
              ? '1px solid rgba(148, 163, 184, 0.1)'
              : '1px solid rgba(148, 163, 184, 0.2)',
          }}
        >
          <CustomButton
            variant="outlined"
            onClick={handleCancelClose}
            sx={{
              borderColor: isDarkMode ? '#475569' : '#d1d5db',
              color: isDarkMode ? '#e2e8f0' : '#374151',
            }}
          >
            Keep Editing
          </CustomButton>
          <CustomButton
            variant="gradient"
            gradient="destructive"
            onClick={handleConfirmClose}
            sx={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              boxShadow: '0 4px 15px rgba(239, 68, 68, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                boxShadow: '0 8px 25px rgba(239, 68, 68, 0.6)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            Discard Changes
          </CustomButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Integration components for the main certificates page
export const useCertificateDialog = () => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  const openCreateDialog = useCallback(() => setCreateDialogOpen(true), []);
  const closeCreateDialog = useCallback(() => setCreateDialogOpen(false), []);

  const openEditDialog = useCallback(certificate => {
    setSelectedCertificate(certificate);
    setEditDialogOpen(true);
  }, []);

  const closeEditDialog = useCallback(() => {
    setEditDialogOpen(false);
    setSelectedCertificate(null);
  }, []);

  return {
    createDialogOpen,
    editDialogOpen,
    selectedCertificate,
    setSelectedCertificate,
    openCreateDialog,
    closeCreateDialog,
    openEditDialog,
    closeEditDialog,
  };
};

// Main dialog components
export const CreateCertificateDialog = React.memo(
  ({ open, onClose, onSave, isDarkMode }) => {
    return (
      <CreateEditCertificateDialog
        open={open}
        onClose={onClose}
        onSave={onSave}
        isDarkMode={isDarkMode}
      />
    );
  }
);

export const EditCertificateDialog = React.memo(
  ({ open, onClose, certificate, onSave, isDarkMode }) => {
    return (
      <CreateEditCertificateDialog
        open={open}
        onClose={onClose}
        certificate={certificate}
        onSave={onSave}
        isDarkMode={isDarkMode}
      />
    );
  }
);

export default CreateEditCertificateDialog;
